import { APIProvider, Map as GoogleMap, Marker, type MapMouseEvent } from '@vis.gl/react-google-maps';
import { LocateFixed, MapPin, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Button } from '../primitives/button';

// Google Maps for job locations (UI-08). With a Maps JavaScript API key the map is interactive; without one (local
// development, a missing key) the view falls back to Google's plain embed and the picker explains it is off.

export interface MapPoint {
  lat: number;
  lng: number;
}

/** Sweden, for a picker with no pin yet */
const SWEDEN = { lat: 62.0, lng: 15.0 };

interface LocationMapProps {
  /** Where the pin goes */
  center: MapPoint;
  /** Maps JavaScript API key; without it a plain Google embed is shown */
  apiKey?: string;
  /** Read out for the map, e.g. "Job location, 412 56" */
  label: string;
  /** A tag over the top-left corner, e.g. the postal code */
  caption?: ReactNode;
  zoom?: number;
  className?: string;
}

/** A Google map with the job's pin. */
export function LocationMap({ center, apiKey, label, caption, zoom = 14, className }: LocationMapProps) {
  return (
    <figure
      aria-label={label}
      className={cn('relative isolate overflow-hidden rounded-xl bg-muted', className ?? 'h-52')}
    >
      {apiKey ? (
        <APIProvider apiKey={apiKey}>
          <GoogleMap
            defaultCenter={center}
            defaultZoom={zoom}
            gestureHandling="cooperative"
            disableDefaultUI
            zoomControl
            className="size-full"
          >
            <Marker position={center} title={label} />
          </GoogleMap>
        </APIProvider>
      ) : (
        <iframe
          title={label}
          src={`https://maps.google.com/maps?q=${center.lat},${center.lng}&z=${zoom}&output=embed`}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="size-full border-0"
        />
      )}
      {caption && (
        <figcaption className="pointer-events-none absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-lg bg-surface/95 px-2.5 py-1.5 text-xs font-semibold text-secondary shadow-sm">
          <MapPin className="size-3.5 text-primary-hover" aria-hidden />
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

interface LocationPickerProps {
  value: MapPoint | null;
  onChange: (point: MapPoint | null) => void;
  apiKey?: string;
  /** Where to look before there is a pin, e.g. the postal code's area */
  initialCenter?: MapPoint | null;
  labels: {
    /** "Click the map where the job is. Drag the pin to adjust." */
    hint: string;
    useMyLocation: string;
    clear: string;
    /** Shown instead of the map when there is no API key */
    unavailable: string;
  };
  className?: string;
}

/** Click the map to set the job's pin, drag it to adjust, or use the device's position. */
export function LocationPicker({
  value,
  onChange,
  apiKey,
  initialCenter,
  labels,
  className,
}: LocationPickerProps) {
  const [locating, setLocating] = useState(false);
  const start = value ?? initialCenter ?? SWEDEN;

  if (!apiKey) {
    return (
      <p className={cn('rounded-xl bg-muted px-4 py-3 text-sm text-muted-foreground', className)}>
        {labels.unavailable}
      </p>
    );
  }

  const pick = (event: MapMouseEvent) => {
    const point = event.detail.latLng;
    if (point) onChange({ lat: round(point.lat), lng: round(point.lng) });
  };

  const locate = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false);
        onChange({ lat: round(position.coords.latitude), lng: round(position.coords.longitude) });
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  };

  return (
    <div className={cn('grid gap-2', className)}>
      <div className="relative h-64 overflow-hidden rounded-xl bg-muted">
        <APIProvider apiKey={apiKey}>
          <GoogleMap
            defaultCenter={start}
            defaultZoom={value ? 15 : initialCenter ? 12 : 4}
            gestureHandling="greedy"
            disableDefaultUI
            zoomControl
            clickableIcons={false}
            onClick={pick}
            className="size-full"
          >
            {value && (
              <Marker
                position={value}
                draggable
                onDragEnd={(event) => {
                  const point = event.latLng;
                  if (point) onChange({ lat: round(point.lat()), lng: round(point.lng()) });
                }}
              />
            )}
          </GoogleMap>
        </APIProvider>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">{labels.hint}</p>
        <div className="flex gap-1.5">
          <Button type="button" variant="outline" size="sm" loading={locating} onClick={locate}>
            <LocateFixed aria-hidden />
            {labels.useMyLocation}
          </Button>
          {value && (
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
              <X aria-hidden />
              {labels.clear}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Five decimals: about a metre, plenty for a house */
const round = (value: number) => Math.round(value * 1e5) / 1e5;
